import DOMPurify from "dompurify";
import MarkdownIt from "markdown-it";
import container from "markdown-it-container";

const EMBED_PROVIDERS = [
    {
        test: (url) =>
            /^https:\/\/(www\.)?(youtube\.com|youtu\.be)\//i.test(url),
        toEmbedUrl: (url) => {
            const match =
                url.match(/[?&]v=([\w-]{11})/) ||
                url.match(/youtu\.be\/([\w-]{11})/) ||
                url.match(/youtube\.com\/embed\/([\w-]{11})/);
            return match
                ? `https://www.youtube-nocookie.com/embed/${match[1]}`
                : null;
        }
    },
    {
        test: (url) => /^https:\/\/(www\.)?tiktok\.com\//i.test(url),
        toEmbedUrl: (url) => {
            const match = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
            return match ? `https://www.tiktok.com/embed/v2/${match[1]}` : null;
        }
    }
];

function buildEmbedHtml(md, rawUrl) {
    const provider = EMBED_PROVIDERS.find((item) => item.test(rawUrl));
    const embedUrl = provider ? provider.toEmbedUrl(rawUrl) : null;
    if (!embedUrl) {
        const safeUrl = md.utils.escapeHtml(rawUrl);
        return `<p class="md-embed-fallback"><a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeUrl}</a></p>`;
    }
    return (
        `<div class="md-embed relative w-full" style="padding-bottom:56.25%">` +
        `<iframe class="absolute inset-0 h-full w-full" src="${embedUrl}" title="Contenido incrustado" ` +
        `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ` +
        `allowfullscreen frameborder="0"></iframe></div>`
    );
}

function createMarkdownIt() {
    const md = new MarkdownIt("default", {
        // HTML crudo permitido: el tenant necesita cosas que markdown no
        // expresa (una imagen con ancho, o envuelta en un enlace). Lo que
        // entra igual pasa por DOMPurify con ALLOWED_TAGS/ALLOWED_ATTR, asi
        // que no abre la puerta a <script> ni a atributos de evento. Debe
        // coincidir en portal, catalog y ecommerce.
        html: true,
        linkify: true,
        // Un `\n` suelto genera <br>. Markdown estandar pide linea en blanco
        // para separar parrafos, pero el tenant escribe en un editor, no en
        // un .md, y espera que Enter corte la linea. Debe coincidir en
        // portal, catalog y ecommerce o el tenant ve algo distinto a lo
        // publicado.
        breaks: true,
        typographer: false
    });

    md.use(container, "cta", {
        validate: (params) => params.trim().match(/^cta\s+.+\|https?:\/\/\S+$/),
        render(tokens, idx) {
            if (tokens[idx].nesting !== 1) return "";
            const match = tokens[idx].info.trim().match(/^cta\s+(.+)$/);
            const [label, url] = match[1].split("|").map((part) => part.trim());
            const safeLabel = md.utils.escapeHtml(label);
            const safeUrl = md.utils.escapeHtml(url);
            return (
                `<div class="md-cta my-4"><a href="${safeUrl}" target="_blank" rel="noopener noreferrer" ` +
                `class="md-cta__button inline-flex items-center justify-center rounded-lg bg-stack-primary px-4 py-2 text-sm font-semibold text-white no-underline">` +
                `${safeLabel}</a></div>`
            );
        }
    });

    md.use(container, "callout", {
        validate: (params) => params.trim() === "callout",
        render(tokens, idx) {
            return tokens[idx].nesting === 1
                ? '<div class="md-callout rounded-lg border-l-4 border-stack-emphasis px-4 py-3">'
                : "</div>";
        }
    });

    md.use(container, "embed", {
        validate: (params) => params.trim().match(/^embed\s+\S+$/),
        render(tokens, idx) {
            if (tokens[idx].nesting !== 1) return "";
            const match = tokens[idx].info.trim().match(/^embed\s+(\S+)$/);
            return buildEmbedHtml(md, match[1].trim());
        }
    });

    return md;
}

const ALLOWED_TAGS = [
    "p",
    "br",
    "hr",
    "strong",
    "em",
    "s",
    "code",
    "pre",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "blockquote",
    "a",
    "img",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "div",
    "iframe"
];

const ALLOWED_ATTR = [
    "href",
    "src",
    "alt",
    "title",
    "target",
    "rel",
    "class",
    "style",
    "width",
    "height",
    "allow",
    "allowfullscreen",
    "frameborder"
];

const ALLOWED_CLASSES = new Set([
    "md-cta",
    "my-4",
    "md-cta__button",
    "inline-flex",
    "items-center",
    "justify-center",
    "rounded-lg",
    "bg-stack-primary",
    "px-4",
    "py-2",
    "text-sm",
    "font-semibold",
    "text-white",
    "no-underline",
    "md-callout",
    "border-l-4",
    "border-stack-emphasis",
    "py-3",
    "md-embed",
    "relative",
    "w-full",
    "absolute",
    "inset-0",
    "h-full",
    "md-embed-fallback"
]);

// `iframe` esta permitido solo para que funcione `:::embed`, que valida la
// URL contra EMBED_PROVIDERS antes de generarlo. Con `html: true` el tenant
// podria escribir un iframe a mano hacia cualquier dominio, asi que se
// revalida el src ya sanitizado y se descarta el que no sea un embed nuestro.
const EMBED_HOSTS =
    /^https:\/\/(www\.youtube-nocookie\.com|www\.tiktok\.com)\//;

function createPurifier() {
    const purifier = DOMPurify();
    purifier.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName !== "IFRAME") return;
        if (!EMBED_HOSTS.test(node.getAttribute("src") || "")) node.remove();
    });
    purifier.addHook("uponSanitizeAttribute", (node, data) => {
        if (data.attrName !== "class") return;
        data.attrValue = data.attrValue
            .split(/\s+/)
            .filter(
                (cls) =>
                    ALLOWED_CLASSES.has(cls) || /^language-[\w-]*$/.test(cls)
            )
            .join(" ");
    });
    return purifier;
}

const md = createMarkdownIt();
const purifier = createPurifier();

export function renderMarkdown(source) {
    const rawHtml = md.render(source || "");
    return purifier.sanitize(rawHtml, { ALLOWED_TAGS, ALLOWED_ATTR });
}
